// The row contract (editable-plan Part 1a, contracts 1.1 and 1.2). A row is one
// dispatchable step: a plain, JSON-safe object with every ROW_KEYS key, optionally keys
// from OPTIONAL_ROW_KEYS, no other key and no undefined value. `params` is the single
// dispatch source, and PARAM_ALLOWLIST says which keys each service and kind accepts.
// Pure: no React, no I/O.
//
// Evolution rule (decision L8): the 1a key sets, their `required` flags and their bounds
// are permanent, because Re-run replays stored executedRows indefinitely. Later parts add
// only optional keys (a new top-level key goes into OPTIONAL_ROW_KEYS) and every new
// allowlist entry declares `log`. Checkpoint-id lists (GT4SD_CHECKPOINTS values, the keys
// of GT4SD_RT_PROPERTY_TOKENS) are live data, not part of the key set.
import { GT4SD_CHECKPOINTS, GT4SD_RT_PROPERTY_TOKENS, GT4SD_RT_TARGET_RANGE } from '../../config/gt4sdCheckpoints';
import {
  MATTERGEN_CHECKPOINT_CONDITIONING,
  MATTERGEN_REQUIRED_CONDITIONING,
  mattergenPropertyError,
} from '../../config/mattergenConditioning';
import { MATTERGEN_BUILTIN_CHECKPOINTS } from '../../components/mattergen/mattergenCheckpoints';
import { SERVICES } from '../router/modelRouter';
import { fm4mOversample } from '../../utils/generationBounds';

export const ROW_SERVICES = Object.freeze(['mattergen', 'fm4m', 'gt4sd']);
export const ROW_KINDS = Object.freeze(['generate', 'represent']);
export const SERVICE_DOMAINS = Object.freeze({
  mattergen: Object.freeze(['inorganic_crystal']),
  fm4m: Object.freeze(['small_molecule', 'polymer']),
  gt4sd: Object.freeze(['small_molecule']),
});
export const ROW_ORIGINS = Object.freeze(['router', 'expert']);
export const DISPATCH_TARGETS = Object.freeze(['builtin', 'custom-weights', 'custom-endpoint', 'byoc']);
export const ROW_KEYS = Object.freeze(['id','service','domain','kind','target','customModelId','candidates','chunkSize','params','steering','origin','dispatchTarget','adjustments']);
export const OPTIONAL_ROW_KEYS = Object.freeze([]);   // top-level keys added after 1a
export const MAX_ROW_CANDIDATES = 1024;            // paid plan max; the plan max itself is enforced by fitRunTotal
export const FM4M_GENERATORS = Object.freeze(['bart']);
export const FM4M_REPRESENTATION_MODELS = Object.freeze(['mol-xl', 'pos', 'Mordred', 'MorganFingerprint']);
export const FM4M_FILTER_KEYS = Object.freeze(['molecular_weight','logP','qed','sa_score','hbd','hba','tpsa','ro5_violations','rotatable_bonds','ring_count','aromatic_rings','heavy_atoms','fraction_sp3']);
export const FM4M_FEATURIZE_CHUNK = Object.freeze({ min: 1, max: 200, default: 25 });
export const GT4SD_ALGORITHMS = Object.freeze(['hugging_face', 'regression_transformer']);
export const STEERING_VALUE_KEYS = Object.freeze(['diffusion_guidance_factor', 'share', 'filters', 'target_property', 'target_value']);

const RETURN_MODES = Object.freeze(['file', 'inline']);
const INLINE_ONLY = Object.freeze({ return_mode: 'inline' });
const RT_ONLY = Object.freeze(['regression_transformer']);

// Per-service param allowlist. Each entry: `type` and its bounds, `required` (true, false,
// or 'when_properties': required when params.properties is non-empty), `targets` (the row
// targets the key is allowed for; absent = every target), `when` (the key is allowed, and
// when required needed, only while those params hold) and `log` ('seed' marks keys whose
// values carry seed SMILES; Part 3 logs them as { hasSeed, seedCount }). No defaults:
// buildRow owns every default. A key not listed is invalid.
export const PARAM_ALLOWLIST = Object.freeze({
  mattergen: Object.freeze({
    properties: Object.freeze({ type: 'mattergen_properties', required: true, log: 'value' }),
    diffusion_guidance_factor: Object.freeze({ type: 'number', min: 0.1, max: 10, required: 'when_properties', log: 'value' }),
    record_trajectories: Object.freeze({ type: 'boolean', required: true, log: 'value' }),
  }),
  fm4m_generate: Object.freeze({
    filters: Object.freeze({ type: 'fm4m_filters', keys: FM4M_FILTER_KEYS, required: true, log: 'value' }),
    oversample_multiplier: Object.freeze({ type: 'integer', min: 1, max: 10, required: true, log: 'value' }),
    max_length: Object.freeze({ type: 'integer', min: 8, max: 2048, required: true, log: 'value' }),
    featurization_model: Object.freeze({ type: 'enum', values: FM4M_REPRESENTATION_MODELS, required: true, log: 'value' }),
    return_mode: Object.freeze({ type: 'enum', values: RETURN_MODES, required: true, log: 'value' }),
    max_items_inline: Object.freeze({ type: 'integer', min: 1, max: 1000, required: true, when: INLINE_ONLY, log: 'value' }),
  }),
  fm4m_represent: Object.freeze({
    seed_smiles: Object.freeze({ type: 'smiles_list', minItems: 1, maxItems: 1000, minLength: 1, maxLength: 1000, required: true, log: 'seed' }),
    return_mode: Object.freeze({ type: 'enum', values: RETURN_MODES, required: true, log: 'value' }),
    max_items_inline: Object.freeze({ type: 'integer', min: 1, max: 1000, required: true, when: INLINE_ONLY, log: 'value' }),
  }),
  gt4sd: Object.freeze({
    algorithm_version: Object.freeze({ type: 'gt4sd_checkpoint', required: true, log: 'value' }),
    target_property: Object.freeze({ type: 'gt4sd_rt_token', required: false, targets: RT_ONLY, log: 'value' }),
    // Part D (D-D2): the service requires target_value for every RT request
    // (the silent 2.5 default is gone) and rejects anything outside
    // GT4SD_RT_TARGET_RANGE with a 422. Mirrored here so a row that cannot run
    // is never built, stored and dispatched first.
    target_value: Object.freeze({ type: 'number', min: GT4SD_RT_TARGET_RANGE[0], max: GT4SD_RT_TARGET_RANGE[1], required: true, targets: RT_ONLY, log: 'value' }),
    target_smiles: Object.freeze({ type: 'string', minLength: 1, maxLength: 1000, required: false, targets: RT_ONLY, log: 'seed' }),
  }),
});

const ROW_ID_RE = /^[A-Za-z0-9_-]{1,32}$/;
const PRIORITIES = Object.freeze(['must', 'prefer', 'optional']);
const MATTERGEN_CHUNK_STEP = 8;
const MATTERGEN_CHUNK_MAX = 256;
const CUSTOM_MODEL_ID_MAX = 128;
const MAX_DERIVED_FROM = 32;
const DERIVED_FROM_TARGET_MAX = 64;
const STEERING_KEYS = Object.freeze(['derivedFrom', 'values']);
const DERIVED_FROM_KEYS = Object.freeze(['index', 'target', 'priority']);
const ADJUSTMENT_KEYS = Object.freeze(['roundedFrom', 'scaledFrom']);
const FILTER_BOUND_KEYS = Object.freeze(['min', 'max']);

const has = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key);
const isFiniteNumber = (v) => typeof v === 'number' && Number.isFinite(v);
const isPositiveInteger = (v) => Number.isInteger(v) && v > 0;

function isPlainObject(v) {
  if (v === null || typeof v !== 'object' || Array.isArray(v)) return false;
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
}

/** The PARAM_ALLOWLIST entry name for a row, or null when service or kind is invalid. */
function allowlistNameOf(row) {
  if (row.service === 'mattergen' && row.kind === 'generate') return 'mattergen';
  if (row.service === 'gt4sd' && row.kind === 'generate') return 'gt4sd';
  if (row.service === 'fm4m' && row.kind === 'generate') return 'fm4m_generate';
  if (row.service === 'fm4m' && row.kind === 'represent') return 'fm4m_represent';
  return null;
}

/** The TARGETS table: what `target` may be for the row's service and kind. */
function targetError(row) {
  const ok = (list) => typeof row.target === 'string' && list.includes(row.target);
  if (row.service === 'mattergen') return ok(MATTERGEN_BUILTIN_CHECKPOINTS) ? null : 'must be a built-in MatterGen checkpoint';
  if (row.service === 'gt4sd') return ok(GT4SD_ALGORITHMS) ? null : `must be one of ${GT4SD_ALGORITHMS.join(', ')}`;
  if (row.kind === 'represent') return ok(FM4M_REPRESENTATION_MODELS) ? null : `must be one of ${FM4M_REPRESENTATION_MODELS.join(', ')}`;
  return ok(FM4M_GENERATORS) ? null : `must be one of ${FM4M_GENERATORS.join(', ')}`;
}

function whenHolds(when, params) {
  return !when || Object.entries(when).every(([key, value]) => params[key] === value);
}

function notAllowedReason(spec) {
  if (spec.targets) return `is allowed only for ${spec.targets.join(', ')}`;
  const [[key, value]] = Object.entries(spec.when);
  return `is allowed only when ${key} is "${value}"`;
}

function isRequired(spec, params) {
  if (spec.required === 'when_properties') return isPlainObject(params.properties) && Object.keys(params.properties).length > 0;
  return spec.required === true;
}

function rangeText(spec) {
  return `from ${spec.min} to ${spec.max}`;
}

/** [subpath, message] pairs for one param value; subpath is appended to `params.<key>`. */
function paramValueErrors(spec, value, row) {
  switch (spec.type) {
    case 'boolean':
      return typeof value === 'boolean' ? [] : [['', 'must be true or false']];
    case 'integer':
      return Number.isInteger(value) && value >= spec.min && value <= spec.max
        ? [] : [['', `must be an integer ${rangeText(spec)}`]];
    case 'number':
      if (spec.min === undefined) return isFiniteNumber(value) ? [] : [['', 'must be a finite number']];
      return isFiniteNumber(value) && value >= spec.min && value <= spec.max
        ? [] : [['', `must be a finite number ${rangeText(spec)}`]];
    case 'string':
      return typeof value === 'string' && value.length >= spec.minLength && value.length <= spec.maxLength
        ? [] : [['', `must be a string of ${spec.minLength} to ${spec.maxLength} characters`]];
    case 'enum':
      return spec.values.includes(value) ? [] : [['', `must be one of ${spec.values.join(', ')}`]];
    case 'smiles_list': {
      if (!Array.isArray(value) || value.length < spec.minItems || value.length > spec.maxItems) {
        return [['', `must be a list of ${spec.minItems} to ${spec.maxItems} SMILES`]];
      }
      const bad = value.findIndex((s) => typeof s !== 'string' || s.trim().length < spec.minLength || s.trim().length > spec.maxLength);
      return bad < 0 ? [] : [[`[${bad}]`, `must be a SMILES string of ${spec.minLength} to ${spec.maxLength} characters`]];
    }
    case 'mattergen_properties': {
      if (!isPlainObject(value)) return [['', 'must be an object']];
      const allowed = MATTERGEN_CHECKPOINT_CONDITIONING[row.target];
      const errors = [];
      for (const [key, v] of Object.entries(value)) {
        if (!allowed.includes(key)) {
          errors.push([`.${key}`, `is not a conditioning key of checkpoint ${row.target}`]);
        } else {
          const err = mattergenPropertyError(key, v);
          if (err) errors.push([`.${key}`, err]);
        }
      }
      const required = has(MATTERGEN_REQUIRED_CONDITIONING, row.target) ? MATTERGEN_REQUIRED_CONDITIONING[row.target] : [];
      for (const key of required) {
        if (!has(value, key)) errors.push([`.${key}`, `is required on checkpoint ${row.target}`]);
      }
      return errors;
    }
    case 'fm4m_filters': {
      if (!isPlainObject(value)) return [['', 'must be an object']];
      const errors = [];
      for (const [key, f] of Object.entries(value)) {
        if (!spec.keys.includes(key)) {
          errors.push([`.${key}`, 'is not a filter key']);
        } else if (!isPlainObject(f) || Object.keys(f).length === 0
          || !Object.keys(f).every((k) => FILTER_BOUND_KEYS.includes(k) && isFiniteNumber(f[k]))) {
          errors.push([`.${key}`, 'must be { min?, max? } with at least one finite bound']);
        } else if (has(f, 'min') && has(f, 'max') && f.min > f.max) {
          errors.push([`.${key}`, 'min must not be above max']);
        }
      }
      return errors;
    }
    case 'gt4sd_checkpoint': {
      const list = (has(GT4SD_CHECKPOINTS, row.target) && GT4SD_CHECKPOINTS[row.target]) || [];
      return list.includes(value) ? [] : [['', `must be an available ${row.target} checkpoint`]];
    }
    case 'gt4sd_rt_token': {
      const version = row.params.algorithm_version;
      const tokens = (typeof version === 'string' && has(GT4SD_RT_PROPERTY_TOKENS, version) && GT4SD_RT_PROPERTY_TOKENS[version]) || {};
      return Object.values(tokens).includes(value) ? [] : [['', 'must be a property token of the checkpoint, for example "logp"']];
    }
    default:
      return [['', 'has no validation rule']];
  }
}

function paramsErrors(row) {
  const params = row.params;
  if (!isPlainObject(params)) return ['params: must be an object'];
  const allow = PARAM_ALLOWLIST[allowlistNameOf(row)];
  const errors = [];
  for (const key of Object.keys(params)) {
    if (!has(allow, key)) errors.push(`params.${key}: is not an allowed key`);
  }
  for (const [key, spec] of Object.entries(allow)) {
    const present = has(params, key);
    const applies = (!spec.targets || spec.targets.includes(row.target)) && whenHolds(spec.when, params);
    if (!applies) {
      if (present) errors.push(`params.${key}: ${notAllowedReason(spec)}`);
    } else if (!present) {
      if (isRequired(spec, params)) errors.push(`params.${key}: is required`);
    } else {
      for (const [sub, message] of paramValueErrors(spec, params[key], row)) errors.push(`params.${key}${sub}: ${message}`);
    }
  }
  if (row.target === 'regression_transformer') {
    // Only the target_property half remains: target_value is required outright for RT
    // (Part D, D-D2), so the old "is required with target_property" line only ever
    // duplicated the required-key error on the same key.
    if (has(params, 'target_value') && !has(params, 'target_property')) errors.push('params.target_property: is required with target_value');
  }
  return errors;
}

function steeringErrors(steering) {
  if (!isPlainObject(steering)) return ['steering: must be an object'];
  const errors = [];
  for (const key of Object.keys(steering)) {
    if (!STEERING_KEYS.includes(key)) errors.push(`steering.${key}: is not a steering key`);
  }
  const { derivedFrom, values } = steering;
  if (!Array.isArray(derivedFrom) || derivedFrom.length > MAX_DERIVED_FROM) {
    errors.push(`steering.derivedFrom: must be a list of at most ${MAX_DERIVED_FROM} entries`);
  } else {
    derivedFrom.forEach((entry, i) => {
      const at = `steering.derivedFrom[${i}]`;
      if (!isPlainObject(entry) || !Object.keys(entry).every((k) => DERIVED_FROM_KEYS.includes(k))) {
        errors.push(`${at}: must be { index, target, priority }`);
        return;
      }
      if (!Number.isInteger(entry.index) || entry.index < 0) errors.push(`${at}.index: must be an integer >= 0`);
      if (typeof entry.target !== 'string' || entry.target.length < 1 || entry.target.length > DERIVED_FROM_TARGET_MAX) {
        errors.push(`${at}.target: must be a string of 1 to ${DERIVED_FROM_TARGET_MAX} characters`);
      }
      if (!PRIORITIES.includes(entry.priority)) errors.push(`${at}.priority: must be one of ${PRIORITIES.join(', ')}`);
    });
  }
  if (!isPlainObject(values)) {
    errors.push('steering.values: must be an object');
  } else {
    for (const key of Object.keys(values)) {
      if (!STEERING_VALUE_KEYS.includes(key)) errors.push(`steering.values.${key}: is not a steering value key`);
    }
  }
  return errors;
}

function adjustmentsErrors(adjustments) {
  if (!isPlainObject(adjustments)) return ['adjustments: must be an object'];
  const errors = [];
  for (const [key, value] of Object.entries(adjustments)) {
    if (!ADJUSTMENT_KEYS.includes(key)) errors.push(`adjustments.${key}: is not an adjustments key`);
    else if (!isPositiveInteger(value)) errors.push(`adjustments.${key}: must be a positive integer`);
  }
  return errors;
}

/** Firestore safety: no array directly inside an array, no undefined, nothing that is not JSON. */
function safetyErrors(value, path, insideArray, ancestors, out) {
  if (value === undefined) {
    out.push(`${path}: must not be undefined`);
  } else if (typeof value === 'function' || typeof value === 'symbol' || typeof value === 'bigint') {
    out.push(`${path}: must be a JSON value`);
  } else if (typeof value === 'number' && !Number.isFinite(value)) {
    out.push(`${path}: must be a finite number`);
  } else if (value !== null && typeof value === 'object') {
    if (ancestors.includes(value)) {
      out.push(`${path}: must not contain itself`);
    } else if (Array.isArray(value)) {
      if (insideArray) out.push(`${path}: must not be an array directly inside an array`);
      else value.forEach((v, i) => safetyErrors(v, `${path}[${i}]`, true, [...ancestors, value], out));
    } else {
      for (const [k, v] of Object.entries(value)) safetyErrors(v, `${path}.${k}`, false, [...ancestors, value], out);
    }
  }
}

function rowErrors(row, optionalRowKeys) {
  if (!isPlainObject(row)) return ['row: must be a plain object'];
  const errors = [];
  // 1. Keys.
  for (const key of ROW_KEYS) {
    if (!has(row, key)) errors.push(`${key}: is required`);
  }
  for (const key of Object.keys(row)) {
    if (!ROW_KEYS.includes(key) && !optionalRowKeys.includes(key)) errors.push(`${key}: is not a row key`);
  }
  // 2. Enums.
  const serviceOk = ROW_SERVICES.includes(row.service);
  if (!serviceOk) errors.push(`service: must be one of ${ROW_SERVICES.join(', ')}`);
  let kindOk = ROW_KINDS.includes(row.kind);
  if (!kindOk) {
    errors.push(`kind: must be one of ${ROW_KINDS.join(', ')}`);
  } else if (row.kind === 'represent' && row.service !== 'fm4m') {
    errors.push('kind: represent is only for fm4m');
    kindOk = false;
  }
  if (serviceOk && !SERVICE_DOMAINS[row.service].includes(row.domain)) {
    errors.push(`domain: must be one of ${SERVICE_DOMAINS[row.service].join(', ')} for ${row.service}`);
  }
  if (!ROW_ORIGINS.includes(row.origin)) errors.push(`origin: must be one of ${ROW_ORIGINS.join(', ')}`);
  if (row.dispatchTarget !== null && !DISPATCH_TARGETS.includes(row.dispatchTarget)) {
    errors.push(`dispatchTarget: must be null or one of ${DISPATCH_TARGETS.join(', ')}`);
  }
  // 3. Id.
  if (typeof row.id !== 'string' || !ROW_ID_RE.test(row.id)) errors.push('id: must be 1 to 32 letters, digits, "_" or "-"');
  // 4. Target.
  const targetErr = serviceOk && kindOk ? targetError(row) : null;
  if (targetErr) errors.push(`target: ${targetErr}`);
  // 5. Custom model.
  if (row.customModelId !== null
    && (typeof row.customModelId !== 'string' || row.customModelId.length < 1 || row.customModelId.length > CUSTOM_MODEL_ID_MAX)) {
    errors.push(`customModelId: must be null or an id of 1 to ${CUSTOM_MODEL_ID_MAX} characters`);
  } else if (row.customModelId !== null && row.kind === 'represent') {
    errors.push('customModelId: must be null for a representation row');
  }
  // 6. Candidates.
  if (row.kind === 'represent') {
    const seeds = isPlainObject(row.params) ? row.params.seed_smiles : undefined;
    if (!Array.isArray(seeds) || row.candidates !== seeds.length) {
      errors.push('candidates: must equal the number of params.seed_smiles');
    }
  } else if (!Number.isInteger(row.candidates) || row.candidates < 1 || row.candidates > MAX_ROW_CANDIDATES) {
    errors.push(`candidates: must be an integer from 1 to ${MAX_ROW_CANDIDATES}`);
  }
  // 7. Chunk size.
  if (row.service === 'mattergen') {
    if (!Number.isInteger(row.chunkSize) || row.chunkSize < MATTERGEN_CHUNK_STEP || row.chunkSize > MATTERGEN_CHUNK_MAX
      || row.chunkSize % MATTERGEN_CHUNK_STEP !== 0) {
      errors.push(`chunkSize: must be a multiple of ${MATTERGEN_CHUNK_STEP} from ${MATTERGEN_CHUNK_STEP} to ${MATTERGEN_CHUNK_MAX}`);
    }
  } else if (row.service === 'fm4m') {
    if (!Number.isInteger(row.chunkSize) || row.chunkSize < FM4M_FEATURIZE_CHUNK.min || row.chunkSize > FM4M_FEATURIZE_CHUNK.max) {
      errors.push(`chunkSize: must be an integer from ${FM4M_FEATURIZE_CHUNK.min} to ${FM4M_FEATURIZE_CHUNK.max}`);
    }
  } else if (row.service === 'gt4sd' && row.chunkSize !== null) {
    errors.push('chunkSize: must be null for gt4sd');
  }
  // 8. Params (needs a valid service, kind and target to pick the allowlist).
  if (serviceOk && kindOk && !targetErr) errors.push(...paramsErrors(row));
  // 9. Steering. 10. Adjustments.
  if (has(row, 'steering')) errors.push(...steeringErrors(row.steering));
  if (has(row, 'adjustments')) errors.push(...adjustmentsErrors(row.adjustments));
  // 11. Firestore safety.
  for (const [key, value] of Object.entries(row)) safetyErrors(value, key, false, [row], errors);
  return errors;
}

function validateRowWith(row, { optionalRowKeys = OPTIONAL_ROW_KEYS } = {}) {
  let errors;
  try {
    errors = rowErrors(row, optionalRowKeys);
  } catch {
    errors = ['row: could not be validated'];
  }
  return { ok: errors.length === 0, errors };
}

/**
 * Pure and never throws. Checks, in order: the key set, the enums, the id, the target
 * (TARGETS table), customModelId, candidates, chunkSize, params against PARAM_ALLOWLIST
 * with the cross-key rules, steering, adjustments, and Firestore safety.
 * @returns {{ ok: boolean, errors: string[] }} each error is "<path>: <reason>".
 */
export function validateRowParams(row) {
  return validateRowWith(row);
}

// Test seam (the db.js `_testing` pattern): validation against a stub OPTIONAL_ROW_KEYS list.
export const _testing = Object.freeze({ validateRowWith });

/** The smallest id `${prefix}<n>` (n = 1, 2, ...) that no row in `rows` uses. 1b's Add a step
 * and Refine use prefix 'x'. */
export function newRowId(rows, prefix) {
  const used = new Set((Array.isArray(rows) ? rows : []).map((r) => r && r.id));
  let n = 1;
  while (used.has(`${prefix}${n}`)) n += 1;
  return `${prefix}${n}`;
}

/** The one deep-copy helper: JSON.parse(JSON.stringify(value)), so undefined values are
 * dropped. undefined in gives undefined out. */
export function cloneJson(value) {
  return value === undefined ? undefined : JSON.parse(JSON.stringify(value));
}

/** cloneJson, typed for rows. */
export function cloneRow(row) {
  return cloneJson(row);
}

/** What "changed from router" compares, by deep equality that ignores object key order. It
 * leaves out id, steering, origin, dispatchTarget and adjustments. */
export function rowComparable(row) {
  const r = row || {};
  return cloneJson({
    service: r.service, kind: r.kind, domain: r.domain, target: r.target, customModelId: r.customModelId,
    candidates: r.candidates, chunkSize: r.chunkSize, params: r.params,
  });
}

function highestPriority(priorities) {
  let best = PRIORITIES.length;
  for (const p of priorities) {
    const rank = PRIORITIES.indexOf(p);
    if (rank >= 0 && rank < best) best = rank;
  }
  return PRIORITIES[best] || 'none';
}

/**
 * 'must' > 'prefer' > 'optional', else 'none'. A row whose steering.derivedFrom is non-empty
 * (2a) takes its highest priority there. Otherwise MatterGen and GT4SD rows take the highest
 * priority of the spec targets whose lowercase name is one of the lowercase properties of
 * SERVICES[row.service].variants[row.target], which is what callPriority gave the router's
 * call before 1a. FM4M rows are 'none'.
 */
export function rowPriority(row, targets) {
  const derivedFrom = row && row.steering && row.steering.derivedFrom;
  if (Array.isArray(derivedFrom) && derivedFrom.length > 0) return highestPriority(derivedFrom.map((d) => d && d.priority));
  if (!row || row.service === 'fm4m' || !has(SERVICES, row.service)) return 'none';
  const variants = SERVICES[row.service].variants;
  const variant = typeof row.target === 'string' && has(variants, row.target) ? variants[row.target] : null;
  const properties = new Set(((variant && variant.properties) || []).map((p) => String(p).toLowerCase()));
  const covered = (Array.isArray(targets) ? targets : [])
    .filter((t) => properties.has(String((t && t.name) || '').toLowerCase()));
  return highestPriority(covered.map((t) => t.priority));
}

/** The fitRunTotal item for one row. Representation rows charge 0 (chargeOf reads kind).
 * FM4M generation carries the oversample its job sends and the service charges. */
export function runTotalItemForRow(row, targets) {
  const item = { service: row.service, budget: row.candidates, priority: rowPriority(row, targets), kind: row.kind };
  if (row.service === 'fm4m' && row.kind === 'generate') {
    const params = row.params || {};
    item.oversample = fm4mOversample({ filters: params.filters, oversample_multiplier: params.oversample_multiplier });
  }
  return item;
}

/**
 * Maps a stored row's live-data values onto today's lists before validation (1.2.6): Re-run
 * (rerunRun) and restoreReviewState apply it before validateRowParams, so a GT4SD checkpoint
 * id retired after the row was stored does not reject the row. For service 'gt4sd', with
 * list = GT4SD_CHECKPOINTS[row.target]:
 * - a non-empty list: an algorithm_version not in it becomes list[0];
 * - a missing or empty list (an algorithm with no available checkpoint): algorithm_version is
 *   left unchanged, and the list is never indexed;
 * - a regression_transformer target_property that is not a value of
 *   GT4SD_RT_PROPERTY_TOKENS[<resulting version>] drops the RT pair (target_property, target_value).
 * Every plain-object row comes back as a deep copy. Anything else is returned untouched.
 * It never throws.
 */
export function normalizeReplayRow(row) {
  if (!isPlainObject(row)) return row;
  let out;
  try {
    out = cloneJson(row);
  } catch {
    return row;
  }
  if (out.service !== 'gt4sd' || !isPlainObject(out.params)) return out;
  const params = out.params;
  const list = typeof out.target === 'string' && has(GT4SD_CHECKPOINTS, out.target) ? GT4SD_CHECKPOINTS[out.target] : null;
  if (Array.isArray(list) && list.length > 0 && !list.includes(params.algorithm_version)) {
    params.algorithm_version = list[0];
  }
  if (out.target === 'regression_transformer' && has(params, 'target_property')) {
    const version = params.algorithm_version;
    const tokens = (typeof version === 'string' && has(GT4SD_RT_PROPERTY_TOKENS, version) && GT4SD_RT_PROPERTY_TOKENS[version]) || {};
    if (!Object.values(tokens).includes(params.target_property)) {
      delete params.target_property;
      delete params.target_value;
    }
  }
  return out;
}
